/**
 * Alerts Context - Global state for real-time WebSocket alerts
 */

import React, { createContext, useContext, useCallback } from 'react';
import { useWebSocket, type Alert, type AlertType } from '../hooks/useWebSocket';

interface AlertsContextValue {
  connected: boolean;
  connecting: boolean;
  error: string | null;
  alerts: Alert[];
  subscribeTokens: (tokens: string[]) => void;
  unsubscribeTokens: (tokens: string[]) => void;
  subscribeWallets: (wallets: string[]) => void;
  unsubscribeWallets: (wallets: string[]) => void;
  clearAlerts: () => void;
  dismissAlert: (alertId: string) => void;
  connect: () => void;
  disconnect: () => void;
}

const AlertsContext = createContext<AlertsContextValue | null>(null);

interface AlertsProviderProps {
  children: React.ReactNode;
  walletAddress?: string;
  onAlert?: (alert: Alert) => void;
}

export function AlertsProvider({ children, walletAddress, onAlert }: AlertsProviderProps) {
  const handleAlert = useCallback((alert: Alert) => {
    // Show browser notification if enabled
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(alert.title, {
        body: alert.message,
        icon: '/favicon.ico',
        tag: alert.id,
      });
    }

    onAlert?.(alert);
  }, [onAlert]);

  const ws = useWebSocket({
    walletAddress,
    onAlert: handleAlert,
    autoConnect: true,
  });

  return (
    <AlertsContext.Provider value={ws}>
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlerts() {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }
  return context;
}

export type { Alert, AlertType };
